/**
 * Intake-carry channel (W21 rung ②) — a NEUTRAL, prompt-only hand-off from
 * the chat thread composer to the home composer, where the intake read-back
 * cards live.
 *
 * Why not the featured-outcome draft: that channel is SKU-scoped — its
 * `outcomeIntent` drives flagship-workflow side effects in the hero composer
 * (clarification flows, draft-change callbacks), so carrying a pasted 전문
 * 필드 표 through it would mislabel a document as an outcome selection. This
 * channel carries exactly one thing: the text the developer pasted, scoped to
 * the principal who pasted it.
 *
 * Validation deltas from the outcome draft, both REQUIRED by the artifact
 * class (documents, not prompts):
 *  - tabs/newlines are allowed (a field table IS tab-separated lines; the
 *    outcome draft's control-character ban would reject every S2 paste);
 *  - the size limit is 200k (a legacy JSP source or a multi-document paste
 *    routinely exceeds the 12k prompt limit).
 */

const STORAGE_KEY = 'cogo.studio.intake-carry.v1'
const SCHEMA_VERSION = 'cogo.studio.intake-carry.v1'
const PROMPT_LIMIT = 200_000
const PRINCIPAL_ID_LIMIT = 256
// Dangerous controls only — \t \n \r are document structure, not danger.
const FORBIDDEN_CONTROL_PATTERN =
  /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/

export interface IntakeCarry {
  schemaVersion: typeof SCHEMA_VERSION
  principalId: string | null
  prompt: string
}

function sessionStorageOrNull(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function validPrompt(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= PROMPT_LIMIT &&
    !FORBIDDEN_CONTROL_PATTERN.test(value)
  )
}

function validPrincipalId(value: unknown): value is string | null {
  return (
    value === null ||
    (typeof value === 'string' &&
      value.trim().length > 0 &&
      value.length <= PRINCIPAL_ID_LIMIT)
  )
}

/** Persist the carry. Returns false (and stores nothing) on invalid input. */
export function writeIntakeCarry(input: {
  principalId: string | null
  prompt: string
}): boolean {
  if (!validPrompt(input.prompt) || !validPrincipalId(input.principalId)) {
    return false
  }
  const storage = sessionStorageOrNull()
  if (!storage) return false
  const carry: IntakeCarry = {
    schemaVersion: SCHEMA_VERSION,
    principalId: input.principalId,
    prompt: input.prompt,
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(carry))
    return true
  } catch {
    return false
  }
}

/**
 * Read AND CLEAR the carry when it belongs to this principal. One-shot by
 * design: a consumed carry must never re-open cards on a later mount, and a
 * different principal's carry is cleared unread (never leaked across
 * accounts in a shared tab).
 */
export function takeIntakeCarry(principalId: string | null): string | null {
  const storage = sessionStorageOrNull()
  if (!storage) return null
  let raw: string | null = null
  try {
    raw = storage.getItem(STORAGE_KEY)
    if (raw === null) return null
    storage.removeItem(STORAGE_KEY)
  } catch {
    return null
  }
  try {
    const parsed = JSON.parse(raw) as Partial<IntakeCarry>
    if (
      parsed?.schemaVersion !== SCHEMA_VERSION ||
      !validPrompt(parsed.prompt) ||
      !validPrincipalId(parsed.principalId ?? null)
    ) {
      return null
    }
    if ((parsed.principalId ?? null) !== principalId) return null
    return parsed.prompt
  } catch {
    return null
  }
}

export function clearIntakeCarry(): void {
  try {
    sessionStorageOrNull()?.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to clear or storage unavailable — either way, no carry remains.
  }
}
